import * as React from 'react';
import GroupLabel from './GroupLabel';
import ElasticsearchAPIConnector from "@elastic/search-ui-elasticsearch-connector";
import { SearchProvider, useSearch } from '@elastic/react-search-ui';
import HitsWidget from "./HitsWidget";
import Filter from "./filter/Filter";
import { useOpenSearchBootstrap } from "../../api";
import Preloader from "../Preloader";
import { chartStyles, withYAxis } from "../../helpers";
import ChartLayout from "../ChartLayout";
import { ShareButton } from "./ShareButton";
import { ResultsBridge } from "./ResultsBridge";
import { parseSharedFilters, filterFieldsOf, stripSharedParams, getSharedParam } from "../shareParams";
import { createInMemoryConnector, inMemoryBootstrap } from "./inMemoryConnector";
import { fetchAllRows } from "./fetchCollection";
import { readChartRows, ensureChartRowsScript } from "../chartRows";

function providerConfig(options) {
  const config = {
    alwaysSearchOnInitialLoad: true,
    initialState: {},
    searchQuery: {
      sort: [],
      result_fields: {},
      facets: {},
      disjunctiveFacets: []
    },
  };

  // A chart is not paginated — it must plot every matching row, so request the
  // whole result window rather than a page. 10k matches the two other
  // full-fetch paths (SearchIndexExporter#export_from_opensearch and
  // fetchCollection's one-shot) and is OpenSearch's default
  // index.max_result_window; asking for more is rejected by the engine.
  // The generic 250 page size inherited from faceted search would silently
  // drop every point past the 250th on this (fallback / ?live=1) path.
  config.initialState.resultsPerPage = options?.displayOptions?.itemsPerPage || 10000;

  if (options?.sorting && Array.isArray(options.sorting) && options.sorting.length > 0) {
    const firstOption = options.sorting[0];
    config.initialState.sortField = firstOption.field;
    config.initialState.sortDirection = firstOption.direction;
  }

  if (Array.isArray(options?.filters)) {
    for (const facetConfig of options.filters) {
      const { attribute } = facetConfig;
      config.searchQuery.facets[attribute] = { type: 'value', size: 500, sort: 'value' };
      config.searchQuery.disjunctiveFacets.push(attribute);
    }
  }

  return config;
}

export function connectorConfig(host, indexes) {
  const config = {
    host,
    index: indexes,
    connectionOptions: {
      headers: {},
    },
  };

  // Prefer the utils-pack helper, fall back to the server-rendered meta tag (no
  // pack dependency), and only set the header when a token is present — an
  // unguarded window.getCsrfToken() throws if the pack hasn't loaded, and an
  // undefined value would be sent as the string "undefined" (Rails 422).
  const csrf = window.getCsrfToken?.() || document.querySelector('meta[name="csrf-token"]')?.content || '';
  if (csrf) config.connectionOptions.headers['X-CSRF-Token'] = csrf;

  const previewToken = document.getElementById('library-preview-token')?.dataset?.value;
  if (previewToken) {
    config.connectionOptions.headers['x-preview-token'] = previewToken;
  }

  return config;
}

function ClearFiltersButton({ filters }) {
  const { clearFilters } = useSearch();

  // Clear only the multi-select facets; every other filter field must survive.
  // Uses filterFieldsOf so a grouped dropdown contributes each of its SECTION
  // fields — those have no `attribute`, so mapping `f.attribute` would drop them
  // and clearFilters would wipe the section selection, leaving the chart mixing
  // sections while the dropdown still displays one.
  const preserved = React.useMemo(() => {
    const keep = filterFieldsOf({ filters: (filters || []).filter(f => f.kind !== 'checkboxes') });
    return keep.filter(Boolean);
  }, [filters]);

  return (
    <div className='d-none faceted-search-clear-filters'>
      <button type='button' onClick={() => clearFilters(preserved)} />
    </div>
  );
}

// Replays shared-permalink filter selections (§1.4) through the live
// setFilter/addFilter API once, after the first search — the same path facet
// clicks use. Applying them via SearchProvider initialState instead left
// react-search-ui's facet aggregations empty (hits came back but every facet was
// undefined), so the sidebar facets hid themselves. Runtime replay reproduces the
// interactive flow, keeping facet counts intact.
function SharedFilterApplier({ filters }) {
  const { setFilter, addFilter } = useSearch();
  const applied = React.useRef(false);
  React.useEffect(() => {
    if (applied.current) return;
    applied.current = true;
    (filters || []).forEach(f => {
      const values = (f.values || []).filter(v => v != null && v !== '');
      if (!values.length) return;
      setFilter(f.field, values[0], f.type || 'any');
      values.slice(1).forEach(v => addFilter(f.field, v, f.type || 'any'));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

export default function OpenSearchProvider({defaultIndex, collectionName, currentGroup, style, lineStyle, title, collapsibleFilters, imageFooter, groups, groupLabel, handleGroupChange}) {
  const host = `${window.location.protocol}//${window.location.host}`;

  const baseFilters = React.useMemo(() => {
    try {
      return currentGroup.filterBy ? JSON.parse(currentGroup.filterBy) : [];
    } catch {
      return [];
    }
  }, [currentGroup.filterBy]);

  // `axis-switcher` (X-axis control) and `grouped-dropdown` (multi-section
  // control) are not single-field search facets — keep them out of the
  // aggregation/bootstrap wiring; they manage their own options and filters.
  const NON_FACET_KINDS = ['axis-switcher', 'grouped-dropdown'];
  const facetFilters = React.useMemo(
    () => (currentGroup.filters || []).filter(f => !NON_FACET_KINDS.includes(f.kind)),
    [currentGroup.filters]
  );

  const facetFields = React.useMemo(
    () => new Set(facetFilters.map(f => f.attribute)),
    [facetFilters]
  );

  // Data-source resolution (D1 / §3.4 + preview parity):
  //  1. Injected `data-chart-for` payload present (published) -> render entirely
  //     in memory: every search, incl. initial facet defaults, is computed
  //     locally, so there is no network round-trip at all.
  //  2. Absent (admin preview / cold cache) -> fetch the whole collection ONCE
  //     and render in memory the same way, so the payload-derived controls
  //     (grouped dropdown / Table / axis switcher) work there too.
  //  3. Fetch empty/errored, or `?live=1` -> fall back to live per-request search.
  const forceLive = getSharedParam('live') === '1';
  const injected = React.useMemo(() => readChartRows(collectionName), [collectionName]);
  const injectedOk = Array.isArray(injected) && injected.length > 0;
  const previewToken = React.useMemo(
    () => document.getElementById('library-preview-token')?.dataset?.value,
    []
  );

  const [fetched, setFetched] = React.useState(null);
  const [fetchState, setFetchState] = React.useState('idle'); // idle|loading|done|empty|error|skip
  React.useEffect(() => {
    if (injectedOk || forceLive) { setFetchState('skip'); return undefined; }
    let cancelled = false;
    setFetchState('loading');
    fetchAllRows(host, defaultIndex, { previewToken })
      .then(rows => {
        if (cancelled) return;
        // Seed the injected-payload script the sidebar / Table / Y-axis read via
        // readChartRows(collectionName), so those work on preview too — done
        // before the children mount (we still render the loader here).
        ensureChartRowsScript(collectionName, rows);
        setFetched(rows);
        setFetchState(rows.length > 0 ? 'done' : 'empty');
      })
      .catch(() => { if (!cancelled) setFetchState('error'); });
    return () => { cancelled = true; };
  }, [injectedOk, forceLive, host, defaultIndex, previewToken, collectionName]);

  const payloadRows = injectedOk ? injected : (fetchState === 'done' ? fetched : null);
  const useInMemory = Array.isArray(payloadRows) && payloadRows.length > 0 && !forceLive;

  // Live per-request path is the fallback only: forced, or the one-shot fetch
  // produced nothing / errored. While the fetch is in flight we show a loader.
  const liveEnabled = forceLive || (!injectedOk && (fetchState === 'empty' || fetchState === 'error'));
  const resolving = !injectedOk && !forceLive && (fetchState === 'idle' || fetchState === 'loading');

  const memoryBootstrap = React.useMemo(
    () => (useInMemory ? inMemoryBootstrap(payloadRows, facetFilters, baseFilters) : null),
    [useInMemory, payloadRows, facetFilters, baseFilters]
  );

  // index=null makes the hook a no-op (no fetch) while resolving or in-memory.
  const live = useOpenSearchBootstrap({
    filters: facetFilters,
    baseFilters,
    host,
    index: (useInMemory || !liveEnabled) ? null : defaultIndex,
  });
  const loading = resolving || (liveEnabled ? live.loading : false);
  const initialFilters = memoryBootstrap ? memoryBootstrap.initialFilters : live.initialFilters;
  const allValues = memoryBootstrap ? memoryBootstrap.allValues : live.allValues;

  const connector = React.useMemo(() => {
    if (useInMemory) {
      return createInMemoryConnector(payloadRows, { baseFilters, facetFields });
    }
    return new ElasticsearchAPIConnector({
      ...connectorConfig(host, defaultIndex),
      interceptSearchRequest: ({ requestBody }, next) => {
        delete requestBody._source;
        if (baseFilters.length > 0) {
          // Every base scope goes into the QUERY, not post_filter, so it narrows
          // the aggregations as well as the hits: they define the rows this chart
          // can plot at all, and a facet counted without them offers values that
          // draw nothing (a spec_decoding scope of off/mtp left a hardware whose
          // rows are all `eagle` in the GPUs facet, selectable and empty).
          // post_filter is left to the connector's own disjunctive faceting.
          const terms = baseFilters.map(f => ({ terms: { [f.field]: f.values } }));
          const query = requestBody.query?.bool
            ? requestBody.query
            : { bool: requestBody.query ? { must: [requestBody.query] } : {} };
          query.bool.filter = [...(query.bool.filter || []), ...terms];
          requestBody.query = query;
        }
        return next(requestBody);
      },
    });
  }, [useInMemory, payloadRows, defaultIndex, host, baseFilters, facetFields]);

  const css = React.useMemo(() => chartStyles(style), [style]);

  // Filter selections pinned by a shared permalink (§1.4) override the bootstrap
  // defaults for the fields they name; other facets keep their defaults. Capture
  // them ONCE, on first render, before stripSharedParams mutates the URL: reading
  // the (mutable) URL inside the memo raced that strip — a recompute after the
  // strip found no params and reset the selection, intermittently. The ref pins
  // the initial group's shared filters so neither the strip nor a group change
  // can drop them (a different group falls back to its bootstrap defaults).
  const sharedFiltersRef = React.useRef(null);
  if (sharedFiltersRef.current === null) {
    sharedFiltersRef.current = {
      group: currentGroup.label,
      filters: parseSharedFilters(filterFieldsOf(currentGroup)),
      // Control params captured here too (before stripSharedParams runs). Reading
      // them in ChartLayout instead races the strip and loses them.
      axis: getSharedParam('axis'),
      y: getSharedParam('y'),
      view: getSharedParam('view'),
    };
  }
  // Applied at runtime by SharedFilterApplier (below) — NOT via initialState.
  // Filters placed in initialState leave react-search-ui's facet aggregations
  // empty, so the sidebar facets hide via useFacetHasData. Replaying them through
  // the live setFilter path (the same one facet clicks use) keeps the counts.
  const sharedFilters = sharedFiltersRef.current.group === currentGroup.label
    ? sharedFiltersRef.current.filters
    : [];
  // Facet filters (model, GPUs) are replayed via setFilter (SharedFilterApplier);
  // grouped-dropdown sections (scenario/sequence) are restored by the dropdown
  // itself (renderFilter's sharedSelection) so its one-section-at-a-time semantics
  // hold — replaying a section via setFilter left a stale sibling section that the
  // dropdown then snapped away from.
  const sharedFacetFilters = sharedFilters.filter(f => facetFields.has(f.field));
  const sharedControls = sharedFiltersRef.current.group === currentGroup.label ? sharedFiltersRef.current : {};

  const searchConfig = React.useMemo(() => {
    // Only real facet fields get aggregations (excludes axis-switcher/grouped-dropdown).
    const base = providerConfig({ ...currentGroup, filters: facetFilters });
    return {
      ...base,
      initialState: { ...base.initialState, filters: initialFilters || [] },
      apiConnector: connector,
      trackUrlState: false,
    };
  }, [currentGroup, facetFilters, initialFilters, connector]);

  // Strip permalink params only after the first search finished loading — by
  // then ChartLayout has mounted and consumed them (filters via initialState,
  // Y-axis/axis/view via ChartLayout). Doing it here (not on NvChart
  // mount) avoids racing the async bootstrap; on a later group change the URL is
  // already clean, so the permalink isn't re-applied.
  React.useEffect(() => {
    if (loading) return;
    stripSharedParams(filterFieldsOf(currentGroup));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (loading) {
    return <Preloader title='Chart'/>;
  }

  // The first SEARCH FACET, by attribute — not the first entry in the filter
  // list, which now also holds axis-switcher / grouped-dropdown controls. That
  // facet is rendered uncascaded (DropdownFacet) and keeps its zero-count values
  // (CheckboxFacet), so the flag has to follow the facets, not the render order.
  const firstFacetAttribute = facetFilters[0]?.attribute ?? null;

  const renderFilter = (filter) => {
    // Hand a grouped dropdown the shared-permalink selection for one of its
    // sections, so it restores that instead of snapping to a default.
    let sharedSelection = null;
    if (filter.kind === 'grouped-dropdown') {
      const sf = sharedFilters.find(f => f.values?.length && (filter.sections || []).some(s => s.field === f.field));
      if (sf) sharedSelection = `${sf.field}:${sf.values[0]}`;
    }
    return (
      <Filter
        key={filter.id ?? filter.attribute}
        config={filter}
        isFirstFacet={!!filter.attribute && filter.attribute === firstFacetAttribute}
        allValues={allValues}
        collectionName={collectionName}
        sharedSelection={sharedSelection}
      />
    );
  };

  return (
    // key= forces SearchProvider to fully reset state when the user switches groups.
    <div className='nv-chart-group nv-chart-group--open-search' key={currentGroup.label}>
      <SearchProvider config={searchConfig}>
        <SharedFilterApplier filters={sharedFacetFilters}/>
        <ChartLayout
          css={css}
          title={title}
          initialAxis={sharedControls.axis ?? null}
          initialYField={sharedControls.y ?? null}
          initialViewMode={sharedControls.view ?? null}
          collapsibleFilters={collapsibleFilters}
          imageFooter={imageFooter}
          currentGroup={currentGroup}
          groups={groups}
          groupLabel={groupLabel}
          handleGroupChange={handleGroupChange}
          renderFilter={renderFilter}
          renderGroupLabel={() => <GroupLabel currentGroup={currentGroup}/>}
          renderHits={(yAxis) => <HitsWidget chartConfig={withYAxis(currentGroup, yAxis)} lineStyle={lineStyle}/>}
          renderShare={(shareState) => <ShareButton {...shareState}/>}
          renderResultsBridge={() => <ResultsBridge/>}
          collectionName={collectionName}
          clearAllOuter={<ClearFiltersButton filters={currentGroup.filters}/>}
        />
      </SearchProvider>
    </div>
  );
}
